import { useState, useEffect } from 'react';
import { Popover, Popconfirm, Typography, Flex, Image, Divider, App } from 'antd';
import AddBankDetails from './AddBankDetails';
import CustomButton from '../../../components/CustomButton/Button';
import { useDispatch, useSelector } from "react-redux";
import { getBusinessDetailedView, removeBank, makeBankPrimary } from '../../../redux/slices/BusinessProfileSlice';
import NoData from "../../../components/NoData/NoData";
import VerifiedIcon from '../../../assets/icons/verified-icon.svg';
import MoreIcon from "../../../assets/icons/more-icon.svg";
import EditIcon from "../../../assets/icons/edit-icon.svg";
import TrashIcon from "../../../assets/icons/trash.svg";
import ProfileSectionLayout from '../../../components/ProfileSectionLayout/ProfileSectionLayout';
import { capitalizeEntireWord } from '../../../helpers/helpers';
import { BTN_TXT, DESC_TXT, GLOBAL_CONSTANTS, TITLE_TXT } from '../../../constants/constants';
import ProfileDetail from '../../../components/Details/ProfileDetail';

const { Text, Title } = Typography;

const BankDetails = () => {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const { busDetailedViewData, loading, error } = useSelector((state) => state.profile1);
  const [isAddNewBank, setIsAddNewBank] = useState(false);
  const [popoverVisible, setPopoverVisible] = useState({});
  const { notification } = App.useApp();

  useEffect(() => {
    dispatch(getBusinessDetailedView());
  }, [dispatch, user?.orgId]);

  useEffect(() => {
    if (error) {
      notification.error({
        message: 'Error',
        description: error,
        placement: 'topRight',
        duration: 3,
      });
    }
  }, [error, notification]);

  const handleDelete = async (accNum) => {
    const res = await dispatch(removeBank({ accNum }));
    if (res.payload.statusCode === 201) {
      dispatch(getBusinessDetailedView(user?.orgId));
      setPopoverVisible({});
    }
  };

  const handleMarkAsPrimary = async (accNum) => {
    const res = await dispatch(makeBankPrimary({ accNum }));
    if (res.payload.statusCode === 201) {
      dispatch(getBusinessDetailedView(user?.orgId));
      setPopoverVisible({});
    }
  };

  const popoverContent = (bank) => (
    <div>
      <Popconfirm
        title={GLOBAL_CONSTANTS.DEL_BANK_CONFIRM}
        okText="Yes"
        cancelText="No"
        onConfirm={() => handleDelete(bank.accNum)}
        getPopupContainer={(trigger) => trigger.parentElement}
      >
        <Flex align="center" gap="small" style={{ cursor: "pointer" }}>
          <Image src={TrashIcon} alt="Delete" preview={false} />
          <Text>{GLOBAL_CONSTANTS.DEL}</Text>
        </Flex>
      </Popconfirm>
      <Divider style={{ margin: "10px 0" }} />
      <Flex align="center" gap="small" onClick={() => handleMarkAsPrimary(bank.accNum)} style={{ cursor: "pointer" }}>
        <Image src={EditIcon} alt="Mark as Primary" preview={false} />
        <Text>{GLOBAL_CONSTANTS.MARK_AS_PRI}</Text>
      </Flex>
    </div>
  );

  const bankDetails = [...(busDetailedViewData?.bankDetails?.filter(bank => !bank.isEscrow) || [])]
    .sort((a, b) => Number(Boolean(b.isPrimary)) - Number(Boolean(a.isPrimary)));

  if (isAddNewBank) {
    return <AddBankDetails goBack={() => setIsAddNewBank(false)} />;
  }

  return (
    <ProfileSectionLayout
      loading={loading}
      withDetailsContainer={false}
      header={
        <Flex align="center" justify='space-between'>
          <Title level={4}>{TITLE_TXT.BANK_DETS}</Title>
          {bankDetails.length < 2 && (
            <CustomButton type="primary" onClick={() => setIsAddNewBank(true)}>
              {BTN_TXT.ADD_NEW_BANK}
            </CustomButton>
          )}
        </Flex>
      }
    >
      {bankDetails.length > 0 ? (
        bankDetails.map((bank) => (
          <div key={bank.bankId} className='bank-details-container'>
            <Flex align='center' justify='space-between' style={{ marginBottom: 30 }}>
              <Flex gap={10} align='center' wrap>
                <Text strong>{capitalizeEntireWord(bank.bankName)}</Text>
                {bank.isVerified && <img src={VerifiedIcon} alt='Verified' />}
                {bank.isPrimary && <div className='primary-bank-tag'>{GLOBAL_CONSTANTS.PRIM_BANK}</div>}
              </Flex>
              {!bank.isPrimary && (
                <Popover
                  content={popoverContent(bank)}
                  trigger="click"
                  arrow={false}
                  placement='bottomRight'
                  open={popoverVisible[bank.bankId]}
                  onOpenChange={(visible) =>
                    setPopoverVisible({ ...popoverVisible, [bank.bankId]: visible })
                  }
                >
                  <Image src={MoreIcon} alt='Actions' preview={false} style={{ cursor: 'pointer' }} />
                </Popover>
              )}
            </Flex>

            <ProfileDetail label={GLOBAL_CONSTANTS.ACC_NUM} value={bank?.accNum} />
            <ProfileDetail label={GLOBAL_CONSTANTS.ACC_HOL_NAME} value={bank?.accHolderName} />
            <ProfileDetail label={GLOBAL_CONSTANTS.ACC_IFSC} value={bank?.ifscCode} />
            <ProfileDetail label={GLOBAL_CONSTANTS.BRA_NAME} value={capitalizeEntireWord(bank?.branchName)} />
          </div>
        ))
      ) : (
        <div className='no-data-container'>
          <NoData desc={DESC_TXT.BANK_DETS_NOT_AVAIL} />
        </div>
      )}
    </ProfileSectionLayout>
  );
};

export default BankDetails;
